import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import {
  Archive,
  ArchiveRestore,
  Building2,
  ChevronDown,
  ExternalLink,
  Settings,
  Trash2,
  Users,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router";
import { ApplicationEnum } from "@repo/utils";
import {
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui";

export type OrganisationAvecStats = FunctionReturnType<
  typeof api.superAdmin.listerOrganisationsAvecStats
>[number];

const clientUrl = process.env.BUN_PUBLIC_PORTAIL_CLIENT_URL ?? "#";

/**
 * Carte d'une organisation sur le dashboard. Les trois écrans les plus
 * consultés (campagnes, crédits, scorecards — si l'organisation a l'entretien
 * structuré) sont en accès direct ; le reste
 * — y compris l'archivage — passe par le menu « Gérer », pour qu'aucune
 * action lourde ne se retrouve au milieu des liens de navigation.
 */
export function CarteOrganisation({ org }: { org: OrganisationAvecStats }) {
  const desarchiver = useMutation(api.superAdmin.desarchiverOrganisation);
  const [confirmerArchivage, setConfirmerArchivage] = useState(false);
  const base = `/organisations/${org._id}`;

  return (
    <div className="bg-card border border-charte-line rounded-surface p-5 flex flex-wrap items-center justify-between gap-4 transition-colors duration-150 hover:border-charte-line-accent">
      <div className="flex items-center gap-4 min-w-0">
        <div className="bg-primary/10 p-2.5 rounded-controle">
          <Building2 size={20} className="text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{org.nom}</span>
            {org.archivedAt && (
              <span className="text-xs bg-accent/10 text-muted-foreground px-2 py-0.5 rounded-full">
                Archivée
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {org.nbCampagnesActives} campagne(s) active(s) · {org.applicationsAutorisees.length} application(s)
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to={`${base}/campagnes`}>Campagnes</Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`${base}/credits`}>Crédits</Link>
        </Button>
        {/* Les scorecards ne servent qu'à l'entretien structuré. */}
        {org.applicationsAutorisees.includes(ApplicationEnum.ENT_STRUCT) && (
          <Button asChild variant="outline" size="sm">
            <Link to={`${base}/scorecards`}>Scorecards</Link>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" aria-label={`Gérer ${org.nom}`}>
              Gérer
              <ChevronDown size={12} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem asChild>
              <Link to={`${base}/users`}>
                <Users />
                Utilisateurs
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`${base}/groupes`}>
                <UsersRound />
                Groupes
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`${base}/modifier`}>
                <Settings />
                Paramètres
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`${base}/corbeille`}>
                <Trash2 />
                Corbeille
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={`${clientUrl}?org=${org._id}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink />
                Voir le portail client
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {org.archivedAt ? (
              <DropdownMenuItem onSelect={() => void desarchiver({ organisationId: org._id })}>
                <ArchiveRestore />
                Désarchiver
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirmerArchivage(true)}>
                <Archive />
                Archiver…
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmerArchivage
        org={org}
        isOpen={confirmerArchivage}
        onClose={() => setConfirmerArchivage(false)}
      />
    </div>
  );
}

function ConfirmerArchivage({
  org,
  isOpen,
  onClose,
}: {
  org: OrganisationAvecStats;
  isOpen: boolean;
  onClose: () => void;
}) {
  const archiver = useMutation(api.superAdmin.archiverOrganisation);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmer = async () => {
    setError(null);
    setSaving(true);
    try {
      await archiver({ organisationId: org._id });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Archiver l'organisation">
      <div className="space-y-4 text-sm">
        <p>
          Les utilisateurs de <strong>{org.nom}</strong> ne pourront plus se
          connecter au portail client.
        </p>
        <p className="text-muted-foreground">
          Aucune donnée n'est supprimée : l'organisation reste consultable ici et
          peut être désarchivée à tout moment.
        </p>
        {error && (
          <p className="text-destructive bg-destructive/10 rounded-controle px-3 py-2">{error}</p>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="destructive" disabled={saving} onClick={() => void confirmer()}>
            {saving ? "Archivage…" : "Archiver"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
